import React, { useState, useEffect } from 'react';
import { Box, Card, Stack, Icon, Typography, Divider, useTheme } from '@mui/material';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { reduxHelper } from '../../../config/reduxHelper';
import TextPropertiesPanel from '../../siteBuilder/properties/TextPropertiesPanel';

const FlexibleGenericCardBlock = ({ block, isSiteBuilder, isActive, handleUpdate, pageFontSx, onDelete, onDuplicate }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { push } = useRouter();
    const { siteBuilderSelectedChild, pageJson } = useSelector((s) => s.reducer);
    const [isHovered, setIsHovered] = useState(false);

    const fontFamily = pageJson?.pageStyle?.fontFamily || 'Roboto';

    useEffect(() => {
        if (isSiteBuilder && isActive && siteBuilderSelectedChild?.uid !== block.uid) {
            dispatch({
                type: reduxHelper.UPDATE_SITE_BUILDER_SELECTED_CHILD,
                payload: { ...block },
            });
        }
    }, [isActive, isSiteBuilder, block, siteBuilderSelectedChild, dispatch]);

    const handleClick = (e) => {
        if (isSiteBuilder) {
            e.stopPropagation();
            // Ensure selection sync on click
            if (siteBuilderSelectedChild?.uid !== block.uid) {
                dispatch({
                    type: reduxHelper.UPDATE_SITE_BUILDER_SELECTED_CHILD,
                    payload: { ...block },
                });
            }
            return;
        }

        if (block.url) {
            if (typeof block.url === 'string') {
                if (block.url.startsWith('http')) {
                    window.open(block.url, '_blank', 'noopener,noreferrer');
                } else {
                    push(block.url);
                }
            } else if (typeof block.url === 'object' && block.url.path) {
                if (block.url.target) {
                    window.open(block.url.path, '_blank', 'noopener,noreferrer');
                } else {
                    push(block.url.path);
                }
            }
        }
    };

    const { content } = block;
    const { title, icon } = content || {};

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                height: '100%'
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {isSiteBuilder && isActive && (
                <Box
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                        position: 'absolute',
                        bottom: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 10000,
                        mb: 1
                    }}
                >
                    <TextPropertiesPanel
                        childOverride={block}
                        onChangeChild={(updated) => handleUpdate(updated)}
                        onDeleteChild={() => onDelete && onDelete(block.id)}
                        onDuplicateChild={() => onDuplicate && onDuplicate(block.id)}
                    />
                </Box>
            )}

            <Card
                onClick={handleClick}
                sx={{
                    width: '100%',
                    borderRadius: "24px",
                    textAlign: "center",
                    borderColor: "#000000",
                    height: '100%',
                    p: 2,
                    cursor: isSiteBuilder ? 'default' : (block.url ? 'pointer' : 'default'),
                    transition: 'all 0.2s',
                    ...block.props?.sx,
                    ...block.props?.style,
                }}
            >
                <Stack spacing={1} alignItems={block.props?.alignItems || 'center'}>
                    {icon && (
                        <>
                            <Icon
                                className={`mdi mdi-${icon}`}
                                sx={{
                                    color: block.props?.style?.iconColor || 'inherit',
                                    fontSize: block.props?.style?.iconSize === 'small' ? 20 : block.props?.style?.iconSize === 'medium' ? 30 : 40,
                                }}
                            />
                            <Divider sx={{ width: '100%', borderColor: block.props?.style?.borderColor || '#00000033' }} />
                        </>
                    )}

                    <Typography
                        contentEditable={isSiteBuilder}
                        suppressContentEditableWarning
                        onBlur={(e) => {
                            const val = e.currentTarget.innerText;
                            handleUpdate({
                                ...block,
                                content: { ...block.content, title: val }
                            });
                        }}
                        sx={{
                            ...((() => {
                                const { backgroundColor, borderColor, borderWidth, borderStyle, iconColor, iconSize, ...titleStyle } = block.props?.style || {};
                                return titleStyle;
                            })()),
                            fontFamily: `${fontFamily}!important`,
                            outline: 'none',
                            cursor: isSiteBuilder ? 'text' : 'inherit',
                        }}
                    >
                        {title || (isSiteBuilder ? 'Card Title' : '')}
                    </Typography>

                    <Typography
                        contentEditable={isSiteBuilder}
                        suppressContentEditableWarning
                        onBlur={(e) => {
                            const val = e.currentTarget.innerText;
                            handleUpdate({
                                ...block,
                                content: { ...block.content, content: val }
                            });
                        }}
                        sx={{
                            ...block.props?.style2,
                            px: 3,
                            py: 1,
                            fontFamily: `${fontFamily}!important`,
                            outline: 'none',
                            cursor: isSiteBuilder ? 'text' : 'inherit',
                        }}
                    >
                        {content?.content || (isSiteBuilder ? 'Card content description here...' : '')}
                    </Typography>
                </Stack>
            </Card>
        </Box>
    );
};

export default FlexibleGenericCardBlock;
